import { supabase } from '../lib/supabaseClient';
import React, { useState, useEffect } from 'react';
import { 
  Users,
  MessageCircle,
  Heart,
  Share2,
  MessagesSquare,
  Loader2,
  Search,
  Filter,
  TrendingUp,
  X
} from 'lucide-react';

const DiscussionCard = ({ discussion, currentUser, onLike }) => {
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(discussion.likes_count || 0);

  useEffect(() => {
    // Existing like check logic remains the same
    setIsLiked(!!discussion.isLiked);
  }, [discussion.isLiked]);

  const handleLike = async () => {
    if (!currentUser) return;
    setIsLiked(!isLiked);
    setLikesCount(prev => prev + (isLiked ? -1 : 1));
    if (onLike) onLike(discussion.id, !isLiked);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(
      Math.floor((date - new Date()) / 86400000),
      'day'
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100">
      <div className="p-6">
        <div className="flex items-start gap-4">
          <img 
            src={discussion.author?.avatar_url || "/api/placeholder/40/40"}
            alt={discussion.author?.full_name || 'User'}
            className="w-12 h-12 rounded-full object-cover border-2 border-gray-100"
          />
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h3 className="font-semibold text-gray-900">
                {discussion.author?.full_name || 'Anonymous User'}
              </h3>
              <span className="text-sm bg-navy-50 text-navy-700 px-3 py-0.5 rounded-full">
                Member
              </span>
              <span className="text-sm text-gray-500">
                • {formatTimestamp(discussion.created_at)}
              </span>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-3">{discussion.title}</h2>
            <p className="text-gray-600 leading-relaxed mb-4">{discussion.content}</p>
          </div>
        </div>
      </div>
      <div className="border-t border-gray-100 px-6 py-3 bg-gray-50 rounded-b-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <button 
              className={`flex items-center gap-2 transition-colors ${
                isLiked ? 'text-navy-700' : 'text-gray-600 hover:text-navy-700'
              }`}
              onClick={handleLike}
              disabled={!currentUser}
            >
              <Heart size={18} className={isLiked ? 'fill-current' : ''} />
              <span className="text-sm font-medium">{likesCount}</span>
            </button>
            <button className="flex items-center gap-2 text-gray-600 hover:text-navy-700 transition-colors">
              <MessageCircle size={18} />
              <span className="text-sm font-medium">{discussion.comments_count || 0}</span>
            </button>
          </div>
          <button className="flex items-center gap-2 text-gray-600 hover:text-navy-700 transition-colors">
            <Share2 size={18} />
            <span className="text-sm font-medium">Share</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const CreateDiscussionModal = ({ isOpen, onClose, currentUser }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) return;
    
    setIsSubmitting(true);
    try {
      const { data, error } = await supabase
        .from('discussions')
        .insert([{
          title,
          content,
          author_id: currentUser.id,
          created_at: new Date().toISOString()
        }])
        .select();

      if (error) throw error;
      
      onClose(data?.[0]);
      setTitle('');
      setContent('');
    } catch (error) {
      console.error('Error creating discussion:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-900">Start a Discussion</h2>
          <button
            onClick={() => onClose()}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Title
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-700 focus:border-transparent"
              placeholder="What would you like to discuss?"
              required
            />
          </div>
          
          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
              Content
            </label>
            <textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={5}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-700 focus:border-transparent"
              placeholder="Share your thoughts..."
              required
            />
          </div>
          
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => onClose()}
              className="px-6 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-navy-700 text-white rounded-lg hover:bg-navy-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Discussion'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CommunityPage = () => {
  const [discussions, setDiscussions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [stats, setStats] = useState({ members: 0, discussions: 0 });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  useEffect(() => {
    // Existing data fetching logic remains the same
    setTimeout(() => {
      setLoading(false);
      setDiscussions([
        {
          id: 1,
          title: "Best practices for enterprise sales",
          content: "Looking for advice on enterprise sales techniques...",
          author: { full_name: "John Doe", avatar_url: "/api/placeholder/40/40" },
          created_at: new Date().toISOString(),
          likes_count: 42,
          comments_count: 12
        }
      ]);
      setStats({ members: 1234, discussions: 567 });
    }, 1000);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-navy-700" />
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="bg-navy-700 rounded-2xl p-8 mb-8 text-white">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <h1 className="text-3xl font-bold mb-2">Sales Community</h1>
              <p className="text-navy-100">Connect and learn from other sales professionals</p>
            </div>
            <button 
              className="bg-white text-navy-700 px-6 py-3 rounded-lg hover:bg-navy-50 transition-colors flex items-center gap-2 font-semibold"
              onClick={() => setIsCreateModalOpen(true)}
            >
              <MessagesSquare size={20} />
              Start Discussion
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Search and Filters */}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search discussions..."
                  className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-navy-700 focus:border-transparent"
                />
              </div>
              <button className="flex items-center gap-2 px-4 py-3 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">
                <Filter size={20} />
                <span>Filters</span>
              </button>
            </div>

            <div className="space-y-6">
              {discussions.map(discussion => (
                <DiscussionCard 
                  key={discussion.id}
                  discussion={discussion}
                  currentUser={currentUser}
                  onLike={() => {}}
                />
              ))}
            </div>
          </div>

          {/* Right Column: Community Stats and Trending */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Community Stats</h2>
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-navy-50 p-2 rounded-lg">
                      <Users size={24} className="text-navy-700" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Total Members</p>
                      <p className="text-xl font-bold text-gray-900">{stats.members.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-navy-50 p-2 rounded-lg">
                      <MessageCircle size={24} className="text-navy-700" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Discussions</p>
                      <p className="text-xl font-bold text-gray-900">{stats.discussions.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Trending Topics</h2>
                <TrendingUp size={24} className="text-navy-700" />
              </div>
              <div className="space-y-3">
                {['Enterprise Sales', 'Cold Outreach', 'Deal Closing', 'Sales Tech'].map(topic => (
                  <button
                    key={topic}
                    className="w-full text-left px-4 py-3 rounded-lg hover:bg-navy-50 text-gray-600 hover:text-navy-700 transition-colors"
                  >
                    #{topic.toLowerCase().replace(' ', '')}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <CreateDiscussionModal 
        isOpen={isCreateModalOpen}
        onClose={(newDiscussion) => {
          setIsCreateModalOpen(false);
          if (newDiscussion) {
            setDiscussions(prev => [newDiscussion, ...prev]);
            setStats(prev => ({
              ...prev,
              discussions: prev.discussions + 1
            }));
          }
        }}
        currentUser={currentUser}
      />
    </div>
  );
};

export default CommunityPage;
